import { useState } from 'react';
import { toast } from '@/lib/toast';
import type { VariantFieldUpdate } from '@/lib/api';
import { useBulkUpdateVariantFields } from '@/hooks/useProducts';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FilterChips } from '@/components/ui/filter-chips';
import type { BulkTargets } from './types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targets: BulkTargets;
  onApplied: () => void;
}

type Field = 'taxable' | 'sku';
type SkuMode = 'prefix' | 'suffix' | 'replace';

const FIELD_CHIPS = [
  { value: 'taxable', label: 'Taxable' },
  { value: 'sku', label: 'SKU' },
];

const SKU_MODE_CHIPS = [
  { value: 'prefix', label: 'Add prefix' },
  { value: 'suffix', label: 'Add suffix' },
  { value: 'replace', label: 'Find & replace' },
];

/**
 * Bulk-edit variant-level fields. Taxable is a flat yes/no applied to every
 * selected variant. SKU is bulk-safe only as a *transform* — a prefix, suffix,
 * or find-and-replace computed per variant from its current SKU — because two
 * variants must never share an identical SKU.
 */
export function BulkVariantFieldsDialog({ open, onOpenChange, targets, onApplied }: Props) {
  const [field, setField] = useState<Field>('taxable');
  const [taxable, setTaxable] = useState('true');
  const [skuMode, setSkuMode] = useState<SkuMode>('prefix');
  const [text, setText] = useState('');
  const [replaceWith, setReplaceWith] = useState('');
  const bulkUpdate = useBulkUpdateVariantFields();
  const pending = bulkUpdate.isPending;

  const reset = () => {
    setText('');
    setReplaceWith('');
  };

  /** Transform one SKU; returns null when the SKU is unchanged. */
  const transformSku = (sku: string | null): string | null => {
    const current = sku ?? '';
    let next = current;
    if (skuMode === 'prefix') next = text + current;
    else if (skuMode === 'suffix') next = current + text;
    else next = current.split(text).join(replaceWith);
    return next === current ? null : next;
  };

  const handleApply = async () => {
    const variants = await targets.resolveVariants().catch((err) => {
      toast.error(`Could not load variants: ${(err as Error).message}`);
      return null;
    });
    if (!variants) return;
    if (variants.length === 0) {
      toast.error('No variants in the selection');
      return;
    }

    let updates: VariantFieldUpdate[];
    if (field === 'taxable') {
      const value = taxable === 'true';
      updates = variants.map((v) => ({
        shopifyVariantId: v.shopify_variant_id,
        shopifyProductId: v.shopify_product_id,
        fields: { taxable: value },
      }));
    } else {
      if (text.trim() === '') {
        toast.error(skuMode === 'replace' ? 'Enter the text to find' : 'Enter the text to add');
        return;
      }
      updates = variants
        .map((v): VariantFieldUpdate | null => {
          const next = transformSku(v.sku);
          return next === null
            ? null
            : {
                shopifyVariantId: v.shopify_variant_id,
                shopifyProductId: v.shopify_product_id,
                fields: { sku: next },
              };
        })
        .filter((u): u is VariantFieldUpdate => u !== null);
      if (updates.length === 0) {
        toast.error('No SKUs would change');
        return;
      }
    }

    try {
      await bulkUpdate.mutateAsync(updates);
      const skipped = variants.length - updates.length;
      toast.success(
        `Updated ${updates.length} variants in Shopify` +
          (skipped > 0 ? ` (${skipped} unchanged)` : ''),
      );
      reset();
      onOpenChange(false);
      onApplied();
    } catch (err) {
      toast.error(`Bulk update failed: ${(err as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit variant fields</DialogTitle>
          <DialogDescription>Applies to {targets.selectionLabel}.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="mb-2 block text-xs font-medium text-muted-foreground">
              Field to edit
            </label>
            <FilterChips
              options={FIELD_CHIPS}
              value={field}
              onChange={(v) => setField(v as Field)}
            />
          </div>

          {field === 'taxable' ? (
            <div>
              <label className="mb-2 block text-xs font-medium text-muted-foreground">
                Charge tax on these variants?
              </label>
              <FilterChips
                options={[
                  { value: 'true', label: 'Taxable' },
                  { value: 'false', label: 'Not taxable' },
                ]}
                value={taxable}
                onChange={setTaxable}
              />
            </div>
          ) : (
            <>
              <div>
                <label className="mb-2 block text-xs font-medium text-muted-foreground">
                  How to change the SKU
                </label>
                <FilterChips
                  options={SKU_MODE_CHIPS}
                  value={skuMode}
                  onChange={(v) => setSkuMode(v as SkuMode)}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  {skuMode === 'prefix'
                    ? 'Prefix to add'
                    : skuMode === 'suffix'
                      ? 'Suffix to add'
                      : 'Find this text'}
                </label>
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={skuMode === 'replace' ? 'text to find' : 'e.g. DO-'}
                  autoFocus
                />
              </div>
              {skuMode === 'replace' && (
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                    Replace with
                  </label>
                  <Input
                    value={replaceWith}
                    onChange={(e) => setReplaceWith(e.target.value)}
                    placeholder="replacement (leave empty to delete)"
                  />
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">
                Computed per variant from its current SKU — variants whose SKU would not
                change are skipped.
              </p>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button className="w-full sm:w-auto" onClick={handleApply} disabled={pending}>
            {pending ? 'Applying…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
